import type { ComponentProps } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  Flex,
  Icon,
  PanelSection,
  Strong,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
  Truncated,
} from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import plur from 'plur';
import type { Delivery } from '@/services/db/delivery/entity';
import { getDeviceStatuses, type PushDeliveryMetaReason } from '@/services/db/delivery/utils';
import { formatError } from '@/utils/format-error';
import { capitalize, isEmptyString } from '@/utils/string';
import { groupDeviceStatuses, type PushDeliveryGroup, type PushDeliveryGroupType } from '../utils/push-results';

type TextColor = ComponentProps<typeof Text>['color'];

/**
 * Per-group text colors (Ember `TYPE_COLORS`): `label` tints the "Sent"/"Failed"
 * heading word, `message` tints the per-device failure reason. Push's `drafted`
 * group is deferred to FPS-3178, so it is absent here.
 */
const TYPE_COLORS: Record<PushDeliveryGroupType, { label?: TextColor; message?: TextColor }> = {
  sent: { label: 'success', message: 'success' },
  bounced: { message: 'caution' },
  suppressed: { message: 'caution' },
  failed: { label: 'critical', message: 'critical' },
  undeliverable: {},
};

const PLATFORM_ICONS: Record<string, IconName> = { web: 'desktop', ios: 'apple', android: 'android' };

function platformIcon(platform: string): IconName {
  return PLATFORM_ICONS[platform] ?? 'desktop';
}

type PushActionResultsProps = { delivery: Delivery };

/**
 * Push device-status results — the sent / undeliverable / bounced / suppressed /
 * failed accordions above the payload preview, ported from the Ember
 * `push-action-results` component. The first group is expanded by default.
 *
 * Deferred to FPS-3178 (needs new data-layer reads): the drafted `recipientDevices`
 * list, and the `multi_app` single-device inline header with its app name.
 */
export function PushActionResults({ delivery }: PushActionResultsProps) {
  const groups = groupDeviceStatuses(getDeviceStatuses(delivery));

  return (
    <>
      {groups.map((group, index) => (
        <PushDeliveryGroupSection key={group.type} group={group} defaultOpen={index === 0} />
      ))}
    </>
  );
}

type PushDeliveryGroupSectionProps = { group: PushDeliveryGroup; defaultOpen: boolean };

/** One collapsible group: a titled accordion over a borderless device table. */
function PushDeliveryGroupSection({ group, defaultOpen }: PushDeliveryGroupSectionProps) {
  const colors = TYPE_COLORS[group.type];
  const preposition = group.type === 'sent' ? 'to' : 'for';
  const summary = `${preposition} ${group.devices.length} ${plur('device', group.devices.length)}`;

  return (
    <PanelSection withBorder withPadding={false}>
      <Accordion defaultOpen={defaultOpen}>
        <AccordionTitle px="250" py="125" data-test-push-results-group-label={group.type}>
          <Text size="s" weight="regular">
            <Strong color={colors.label}>{capitalize(group.type)}</Strong> {summary}
          </Text>
        </AccordionTitle>

        <AccordionContent p="0" pb="025">
          <Table caption="List of recipient push devices" isBorderless>
            <TableThead>
              <TableTr>
                <TableTh aria-hidden h="0" p="0" style={{ width: '1%', whiteSpace: 'nowrap' }} />
                <TableTh aria-hidden h="0" p="0" style={{ maxWidth: 0 }} />
              </TableTr>
            </TableThead>

            <TableTbody>
              {group.devices.map((device, index) => (
                <DeviceRows key={`${device.id}-${index}`} device={device} messageColor={colors.message} />
              ))}
            </TableTbody>
          </Table>
        </AccordionContent>
      </Accordion>
    </PanelSection>
  );
}

type DeviceRowsProps = { device: PushDeliveryMetaReason; messageColor: TextColor };

/** A device's platform-icon + truncated id row, plus an optional failure-reason row. */
function DeviceRows({ device, messageColor }: DeviceRowsProps) {
  return (
    <>
      <TableTr data-test-device-detail>
        <TableTd data-test-device-type>
          <Flex alignItems="center">
            <Icon name={platformIcon(device.platform)} color="subtle" />
          </Flex>
        </TableTd>
        <TableTd style={{ maxWidth: 0 }}>
          <Flex alignItems="center" minW="0">
            <Truncated
              tooltip={device.id}
              family="mono"
              size="xs"
              weight="semibold"
              className="fs-mask"
              data-test-device-id
            >
              {device.id}
            </Truncated>
          </Flex>
        </TableTd>
      </TableTr>

      {isEmptyString(device.detail) ? null : (
        <TableTr>
          <TableTd colSpan={2} data-test-device-delivery-detail>
            <Text color={messageColor}>{formatError(device, { type: 'push' })}</Text>
          </TableTd>
        </TableTr>
      )}
    </>
  );
}
